import {
  BookOpen,
  Boxes,
  BriefcaseBusiness,
  ChevronDown,
  CircleHelp,
  CircleUserRound,
  FlaskConical,
  Gavel,
  GraduationCap,
  Menu,
  PanelLeft,
  PanelLeftClose,
  Scale,
  ScanText,
  Settings,
  Users,
  X,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { TRIBUNALEA_APPLICATIONS } from "@tribunalea/shared";
import { InstrumentaToolsDialog } from "./InstrumentaToolsDialog.jsx";
import { AppLink, useRouter } from "./router.jsx";
import {
  defaultSidebarLayout,
  persistSidebarLayoutPreference,
  readSidebarLayoutPreference,
  SIDEBAR_LAYOUT_STORAGE_KEY,
} from "./sidebar-layout.js";
import { SuiteLogo } from "./SuiteLogo.jsx";

const productIcons = {
  Cuenta: CircleUserRound,
  Praxis: BriefcaseBusiness,
  Instrumenta: ScanText,
  Peritia: Scale,
  Criterium: Gavel,
  Scientia: GraduationCap,
  Civita: Users,
  Lexia: BookOpen,
  Lab: FlaskConical,
};

const comingSoonProducts = TRIBUNALEA_APPLICATIONS
  .filter(({ comingSoon }) => comingSoon)
  .map(({ name }) => ({ name, disabled: true }));

function publicSiteUrl(portalUrl, path) {
  try {
    return new URL(path, portalUrl).toString();
  } catch {
    return path;
  }
}

function useMobileSuiteShell() {
  const [mobile, setMobile] = useState(() => (
    typeof window !== "undefined"
      ? window.matchMedia("(max-width: 1024px)").matches
      : false
  ));

  useEffect(() => {
    const media = window.matchMedia("(max-width: 1024px)");
    const update = () => setMobile(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  return mobile;
}

function useSidebarLayout() {
  const [responsiveLayout, setResponsiveLayout] = useState(() => (
    typeof window === "undefined"
      ? "fixed"
      : defaultSidebarLayout(window.innerWidth)
  ));
  const [preference, setPreference] = useState(() => (
    readSidebarLayoutPreference()
  ));

  useEffect(() => {
    const media = window.matchMedia("(max-width: 1439px)");
    const update = () => setResponsiveLayout(
      media.matches ? "collapsible" : "fixed",
    );
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    const sync = (event) => {
      if (event?.type === "storage" && event.key !== SIDEBAR_LAYOUT_STORAGE_KEY) {
        return;
      }
      const next = readSidebarLayoutPreference();
      if (next) {
        try {
          window.localStorage.setItem(SIDEBAR_LAYOUT_STORAGE_KEY, next);
        } catch {
          // The cookie can still synchronize the preference across products.
        }
        setPreference(next);
      }
    };
    const syncWhenVisible = () => {
      if (document.visibilityState === "visible") sync();
    };
    window.addEventListener("focus", sync);
    window.addEventListener("pageshow", sync);
    window.addEventListener("storage", sync);
    document.addEventListener("visibilitychange", syncWhenVisible);
    return () => {
      window.removeEventListener("focus", sync);
      window.removeEventListener("pageshow", sync);
      window.removeEventListener("storage", sync);
      document.removeEventListener("visibilitychange", syncWhenVisible);
    };
  }, []);

  const layout = preference || responsiveLayout;
  const updateLayout = (next) => {
    persistSidebarLayoutPreference(next);
    setPreference(next);
  };
  return [layout, updateLayout];
}

function ProductLinks({
  hiddenProductNames = [],
  products,
  productName,
  mobile = false,
}) {
  const productEntries = products.filter((product) => product.name !== "Cuenta");
  const configuredNames = new Set(productEntries.map((product) => product.name));
  const hiddenNames = new Set(hiddenProductNames);
  const visibleProducts = [
    ...productEntries,
    ...comingSoonProducts.filter((product) => (
      !configuredNames.has(product.name) && !hiddenNames.has(product.name)
    )),
  ];

  return (
    <nav
      className={mobile ? "suite-mobile-products" : "suite-product-nav"}
      aria-label="Productos de Tribunalea"
    >
      {mobile && <span className="suite-products-kicker">Productos</span>}
      <div
        className={`suite-product-list ${
          mobile ? "suite-mobile-product-grid" : ""
        }`}
      >
        {visibleProducts.map((product) => {
          const Icon = product.icon || productIcons[product.name] || ScanText;
          const active = product.name === productName;
          const className = `suite-product-link ${
            active ? "is-active" : ""
          } ${product.disabled ? "is-disabled" : ""}`;
          const label = product.disabled
            ? `${product.name}, próximamente`
            : product.name;

          if (product.disabled) {
            return (
              <button
                key={product.name}
                type="button"
                className={className}
                aria-label={label}
                title={`${product.name} · Próximamente`}
                disabled
              >
                <Icon size={mobile ? 18 : 20} />
                <span className="suite-product-copy">
                  <span>{product.name}</span>
                  <small>Próximamente</small>
                </span>
              </button>
            );
          }

          return (
            <a
              key={product.name}
              href={product.href}
              className={className}
              aria-current={active ? "page" : undefined}
              title={product.name}
              onClick={(event) => {
                if (active) event.preventDefault();
              }}
            >
              <Icon size={mobile ? 18 : 20} />
              <span className="suite-product-copy">
                <span>{product.name}</span>
              </span>
            </a>
          );
        })}
      </div>
    </nav>
  );
}

function AccountSettingsLink({ href, onClick }) {
  const content = (
    <>
      <Settings size={17} />
      <span>
        <strong>Mi Cuenta</strong>
        <small>Créditos, firma y preferencias</small>
      </span>
    </>
  );
  if (/^https?:\/\//i.test(href)) {
    return (
      <a href={href} className="suite-account-settings-link" onClick={onClick}>
        {content}
      </a>
    );
  }
  return (
    <AppLink to={href} className="suite-account-settings-link" onClick={onClick}>
      {content}
    </AppLink>
  );
}

function AccountMenu({
  active,
  avatar,
  controls,
  href,
  mobile = false,
  onNavigate,
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const closeOnOutsideClick = (event) => {
      if (containerRef.current?.contains(event.target)) return;
      if (event.target instanceof Element && event.target.closest("[class*='cl-']")) {
        return;
      }
      setOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("click", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("click", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  const content = (
    <>
      {avatar?.imageUrl ? (
        <img
          src={avatar.imageUrl}
          alt=""
          className={`suite-account-avatar suite-account-avatar--${
            avatar.type === "organization" ? "organization" : "personal"
          }`}
          draggable="false"
        />
      ) : (
        <CircleUserRound size={20} />
      )}
      <span>Cuenta</span>
      <ChevronDown className="suite-account-menu-chevron" size={14} />
    </>
  );
  const className = mobile
    ? "suite-account-menu-trigger suite-account-menu-trigger--mobile"
    : `suite-product-link ${active ? "is-active" : ""}`;
  const ariaLabel = avatar?.label ? `Cuenta · ${avatar.label}` : "Cuenta";
  const closeMenu = () => {
    setOpen(false);
    onNavigate?.();
  };

  return (
    <div
      className={`suite-account-menu ${mobile ? "is-mobile" : ""} ${
        open ? "is-open" : ""
      }`}
      ref={containerRef}
    >
      <button
        type="button"
        className={className}
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        {content}
      </button>
      {open && (
        <div className="suite-account-menu-panel" role="menu">
          <AccountSettingsLink href={href} onClick={closeMenu} />
          {controls || (
            <span className="suite-demo-account suite-account-menu-unavailable">
              <strong>Sesión no disponible</strong>
              <small>Configurá Clerk para administrar la cuenta</small>
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function WorkspaceBadge({ appearance, compact = false }) {
  const Icon = appearance.organization ? BriefcaseBusiness : CircleUserRound;
  return (
    <div
      className={`suite-workspace-badge ${compact ? "is-compact" : ""}`}
      title={`Espacio activo: ${appearance.label}`}
      aria-label={`Espacio activo: ${appearance.label}`}
      role="group"
    >
      <span className="suite-workspace-badge-avatar" aria-hidden="true">
        {appearance.imageUrl ? (
          <img src={appearance.imageUrl} alt="" draggable="false" />
        ) : <Icon size={16} />}
      </span>
      <span className="suite-workspace-badge-copy">
        <small>Espacio activo</small>
        <strong>{appearance.label}</strong>
      </span>
    </div>
  );
}

function CollapsibleNavigationItem({ item, onNavigate }) {
  const { path } = useRouter();
  const Icon = item.icon;
  const hasChildren = Boolean(item.children?.length);
  const routeActive = Boolean(item.to) && (
    path === item.to
    || item.children?.some((child) => (
      path === child.to || path.startsWith(`${child.to}/`)
    ))
  );
  const [expanded, setExpanded] = useState(() => Boolean(routeActive));

  useEffect(() => {
    if (routeActive) setExpanded(true);
  }, [routeActive, path]);

  return (
    <div
      className={`suite-nav-item ${expanded ? "is-expanded" : ""}`}
    >
      <div className="suite-nav-link-row">
        {item.href ? (
          <a
            href={item.href}
            className="suite-nav-link"
            aria-label={item.label}
            title={item.label}
            onClick={onNavigate}
          >
            <Icon size={18} />
            <span>{item.label}</span>
          </a>
        ) : (
          <AppLink
            to={item.to}
            className="suite-nav-link"
            aria-label={item.label}
            title={item.label}
            onClick={(event) => {
              if (hasChildren) setExpanded(true);
              onNavigate(event);
            }}
          >
            <Icon size={18} />
            <span>{item.label}</span>
          </AppLink>
        )}
        {hasChildren && (
          <button
            type="button"
            className="suite-nav-toggle"
            aria-label={`${expanded ? "Contraer" : "Desplegar"} ${item.label}`}
            aria-expanded={expanded}
            onClick={() => setExpanded((value) => !value)}
          >
            <ChevronDown size={16} />
          </button>
        )}
      </div>
      {hasChildren && expanded ? (
        <div className="suite-nav-children">
          {item.children.map((child) => (
            <AppLink
              key={child.to}
              to={child.to}
              exact={child.exact ?? true}
              className="suite-nav-child-link"
              onClick={onNavigate}
            >
              <span className="suite-nav-child-marker" />
              <span>{child.label}</span>
            </AppLink>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function ProductShell({
  productName,
  products = [],
  navigation = [],
  instrumentaToolsUrl = null,
  allowedInstrumentaToolIds = null,
  hiddenProductNames = [],
  portalUrl,
  accountControls,
  accountAvatar = null,
  workspace = null,
  demo = false,
  accountSettingsHref = "/creditos",
  children,
  supportEmail = "soporte@tribunalea.com",
  privacyUrl = null,
  securityUrl = null,
}) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [collapsedAfterNavigate, setCollapsedAfterNavigate] = useState(false);
  const mobile = useMobileSuiteShell();
  const [sidebarLayout, setSidebarLayout] = useSidebarLayout();
  const collapsibleSidebar = sidebarLayout === "collapsible";
  const organizationWorkspace = workspace?.type === "organization"
    || accountAvatar?.type === "organization";
  const configuredWorkspaceColor = workspace?.branding?.primaryColor;
  const workspaceAccent = organizationWorkspace
    ? (/^#[0-9a-f]{6}$/i.test(configuredWorkspaceColor || "")
      ? configuredWorkspaceColor
      : "#5b5bd6")
    : "#15936f";
  const workspaceAppearance = {
    organization: organizationWorkspace,
    label: organizationWorkspace
      ? workspace?.displayName || accountAvatar?.label || "Organización"
      : "Personal",
    imageUrl: organizationWorkspace
      ? workspace?.branding?.logoUrl || accountAvatar?.imageUrl || ""
      : accountAvatar?.imageUrl || "",
  };
  const resolvedPrivacyUrl = privacyUrl || publicSiteUrl(portalUrl, "/privacidad");
  const resolvedCookiesUrl = publicSiteUrl(portalUrl, "/privacidad#cookies");
  const resolvedSecurityUrl = securityUrl || publicSiteUrl(portalUrl, "/seguridad");

  useEffect(() => {
    if (!collapsibleSidebar) setCollapsedAfterNavigate(false);
  }, [collapsibleSidebar]);

  const handleSidebarNavigate = (event) => {
    event?.currentTarget?.blur?.();
    setMobileOpen(false);
    if (collapsibleSidebar && !mobile) setCollapsedAfterNavigate(true);
  };

  const closeTools = useCallback(() => setToolsOpen(false), []);
  const openTools = () => {
    setMobileOpen(false);
    setToolsOpen(true);
  };

  return (
    <div
      className={`suite-shell ${
        collapsibleSidebar ? "suite-shell--sidebar-collapsible" : ""
      } ${organizationWorkspace ? "suite-shell--organization" : "suite-shell--personal"}`}
      style={{ "--suite-workspace-accent": workspaceAccent }}
    >
      {!mobile && (
        <aside className="suite-rail" aria-label="Navegación de Tribunalea">
          <a
            className="suite-rail-brand"
            href={portalUrl}
            aria-label="Ir al portal de Tribunalea"
            title="Portal de Tribunalea"
          >
            <SuiteLogo productName={productName} compact />
          </a>
          <ProductLinks
            hiddenProductNames={hiddenProductNames}
            products={products}
            productName={productName}
          />
          <div className="suite-rail-account">
            <AccountMenu
              active={productName === "Cuenta"}
              avatar={accountAvatar}
              controls={accountControls}
              href={accountSettingsHref}
            />
          </div>
        </aside>
      )}

      <aside
        className={`suite-sidebar ${
          collapsibleSidebar ? "suite-sidebar--collapsible" : ""
        } ${collapsedAfterNavigate ? "is-navigation-collapsed" : ""
        } ${mobileOpen ? "is-open" : ""}`}
        onPointerEnter={() => {
          if (collapsedAfterNavigate) setCollapsedAfterNavigate(false);
        }}
      >
        <div className="suite-sidebar-top">
          <SuiteLogo productName={productName} />
          <button
            type="button"
            className="suite-icon-button suite-sidebar-close"
            aria-label="Cerrar navegación"
            onClick={() => setMobileOpen(false)}
          >
            <X size={20} />
          </button>
        </div>

        <WorkspaceBadge appearance={workspaceAppearance} />

        {mobile && (
          <ProductLinks
            hiddenProductNames={hiddenProductNames}
            products={products}
            productName={productName}
            mobile
          />
        )}

        {demo && (
          <div className="suite-demo-workspace">
            <span>
              <strong>Vista de demostración</strong>
              <small>Sin identidad conectada</small>
            </span>
          </div>
        )}

        <nav
          className="suite-main-nav"
          aria-label={`Navegación de ${productName}`}
        >
          {navigation.map((section, sectionIndex) => (
            <div
              className={`suite-nav-section ${
                sectionIndex ? "suite-nav-section--spaced" : ""
              }`}
              key={section.label}
            >
              <span className="suite-nav-kicker">{section.label}</span>
              {section.items.map((item) => (
                <CollapsibleNavigationItem
                  key={item.to || item.href}
                  item={item}
                  onNavigate={handleSidebarNavigate}
                />
              ))}
            </div>
          ))}
          {instrumentaToolsUrl
            && (!Array.isArray(allowedInstrumentaToolIds)
              || allowedInstrumentaToolIds.length > 0) && (
            <div className="suite-nav-section suite-nav-section--spaced">
              <span className="suite-nav-kicker">Instrumenta</span>
              <button
                type="button"
                className="suite-nav-link suite-tools-launcher-button"
                aria-haspopup="dialog"
                aria-expanded={toolsOpen}
                aria-label="Abrir herramientas de Instrumenta"
                title="Herramientas"
                onClick={openTools}
              >
                <Boxes size={18} />
                <span>Herramientas</span>
              </button>
            </div>
          )}
        </nav>

        <div className="suite-sidebar-footer">
          <button
            type="button"
            className="suite-sidebar-layout-button"
            title={collapsibleSidebar
              ? "Cambiar a barra lateral fija"
              : "Cambiar a barra lateral contraíble"}
            aria-label={collapsibleSidebar
              ? "Cambiar a barra lateral fija"
              : "Cambiar a barra lateral contraíble"}
            onClick={() => setSidebarLayout(
              collapsibleSidebar ? "fixed" : "collapsible",
            )}
          >
            {collapsibleSidebar
              ? <PanelLeft size={17} />
              : <PanelLeftClose size={17} />}
            <span>
              <strong>{collapsibleSidebar ? "Contraíble" : "Fija"}</strong>
              <small>{collapsibleSidebar
                ? "Se expande al pasar el cursor"
                : "Siempre permanece abierta"}</small>
            </span>
          </button>
          <a href={`mailto:${supportEmail}`} className="suite-support-link">
            <CircleHelp size={17} />
            <span>Ayuda y soporte</span>
          </a>
          <nav className="suite-legal-links" aria-label="Información legal">
            <a href={resolvedPrivacyUrl} target="_blank" rel="noreferrer">
              Privacidad
            </a>
            <span aria-hidden="true">·</span>
            <a href={resolvedCookiesUrl} target="_blank" rel="noreferrer">
              Cookies
            </a>
            <span aria-hidden="true">·</span>
            <a href={resolvedSecurityUrl} target="_blank" rel="noreferrer">
              Seguridad
            </a>
          </nav>
          {mobile && (
            <div className="suite-account-row">
              <AccountMenu
                active={productName === "Cuenta"}
                avatar={accountAvatar}
                controls={accountControls}
                href={accountSettingsHref}
                mobile
                onNavigate={() => setMobileOpen(false)}
              />
            </div>
          )}
        </div>
      </aside>

      {mobileOpen && (
        <button
          className="suite-sidebar-backdrop"
          aria-label="Cerrar navegación"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <main className="suite-content-shell">
        <header className="suite-mobile-header">
          <button
            className="suite-icon-button"
            onClick={() => setMobileOpen(true)}
            aria-label="Abrir navegación"
          >
            <Menu size={22} />
          </button>
          <a
            className="suite-mobile-brand-link"
            href={portalUrl}
            aria-label="Ir al portal de Tribunalea"
          >
            <SuiteLogo productName={productName} />
          </a>
          <WorkspaceBadge appearance={workspaceAppearance} compact />
        </header>
        {children}
      </main>
      {instrumentaToolsUrl
        && (!Array.isArray(allowedInstrumentaToolIds)
          || allowedInstrumentaToolIds.length > 0) && (
        <InstrumentaToolsDialog
          allowedToolIds={allowedInstrumentaToolIds}
          instrumentaUrl={instrumentaToolsUrl}
          onClose={closeTools}
          open={toolsOpen}
        />
      )}
    </div>
  );
}
